import { useState } from 'react';
import { useParams } from 'react-router-dom';
import NavBar from '../navbar/navbar';
import WinInfo from './wininfo';
import { Box } from '@mui/material';
import SelectedFixture from '../roundview/components/selectedfixture/selectedfixture';
import useCouponShare from '../couponShares/useCouponShare';
import useInterval from 'react-useinterval';
import useDetailRound from '../rounds/useDetailRound';
import Progress from '../common/progress';
import { themeC } from '../theme/themehelper';
import { isFixtureAfterGameStart } from '../common/DateTimeHelper';
// @ts-ignore
import { useMediaQuery } from "react-responsive/src";
import useDetailedFixture from "../fixtures/useDetailedFixture";
import { IsRightToLeftTheme, IsShareCouponEnabled, IsOrangeLight } from '../theme/themehelper';
import SlimCoupon from '../slimcoupon/slimcoupon';
import CouponWinHeader from './couponwinheader';
import BestRows from './bestrows';
import RowsStatistics from './rowsstatistics';
import CouponInfo from './couponinfo';
import SurvivorCoupon from './survivorcoupon';
import CashoutButton from '../cashout/cashoutbutton';
import { CashoutConfirmDialog, CashoutResultDialog } from '../cashout/cashoutdialogs';
import useCashout from '../cashout/usecashout';
import { Fixture } from '../models/fixture';
import { CorrectRow, Leg, System } from '../models/couponshares';
import { getTheGameManifest } from '../roundview/components/thegame/thegamemanifest';
import TheGameLiveCoupon from './thegamelivecoupon';

const wrapperSx = {
  display: 'flex',
  ml: 'var(--coupon-left-margin)',
  flexDirection: 'var(--flex-direction)',
  '@media screen and (min-width: 951px)': {
    '& .left-flex': {
      minWidth: '402px',
    },
    '& .right-flex': {
      width: '100%',
    },
  },
  '@media screen and (max-width: 950px)': {
    flexDirection: 'column',
    mt: 0,
  },
};

const topBorderSx = {
  borderBottom: (window.theme === themeC.Lion || IsOrangeLight(window.theme)) ? '1px solid #DDDDDD' : window.theme === themeC.Orange ? '1px solid #575757' : '1px solid #060707',
  height: '1px',
};

const ContentArea = ({ fixture, theme, round }: { fixture: Fixture; theme: string; round: any }) => {
  const { fixtureDetails } = useDetailedFixture(fixture.id) as any;

  if (fixture) {
    const isAfterGameStart = isFixtureAfterGameStart(fixture);
    return <SelectedFixture fixtureDetail={fixtureDetails} isAfterGameStart={isAfterGameStart} roundFixture={fixture} theme={theme} round={round} />;
  }
  return <Progress />;
};

function calcCorrectSigns(row: Leg, correctRow: CorrectRow): number {
  let numCorrect = 0;
  for (let i = 0; i < correctRow.signs.length; i++) {
    if (row.signs[i] === correctRow.signs[i]) {
      numCorrect++;
    }
  }
  return numCorrect;
}

export default function CouponView() {
  const isRightToLeft = IsRightToLeftTheme((window as any).theme);

  const { couponId } = useParams<{ couponId: string }>();
  const isSmallScreen = useMediaQuery({ query: '(max-width: 950px)' });
  const initialFixtureIndexSelected = isSmallScreen ? -1 : 0;
  const [fixtureValue, setFixtureValue] = useState(initialFixtureIndexSelected);

  const couponShareResponse = useCouponShare(couponId as any);
  const roundId = couponShareResponse?.[0]?.coupon?.roundId;
  const detailRoundResponse = useDetailRound(roundId);

  const round = detailRoundResponse.round;
  const couponShare = couponShareResponse[0];
  const cashout = useCashout(couponShare);

  useInterval(() => {
    couponShareResponse[1].reloadCouponShare();
    detailRoundResponse.reloadDetailRound();
  }, 20000);

  if (!couponShare?.coupon) {
    return null;
  }

  if (detailRoundResponse.status === "loading") {
    return <div></div>;
  }

  const fixture = fixtureValue === undefined || !round ? undefined : round.fixtures[fixtureValue];

  const isAfterDeadline = new Date(round.openTo) < (Date.now() as any);

  const deepCoupon = couponShare!.coupon.coupon;
  const detailedBets = deepCoupon?.bets;
  const correctRow = couponShare!.coupon.correctRow;
  const preliminaryOutcome: Record<number, number> = {};

  let maxCorrectSigns = 0;

  if (deepCoupon && detailedBets && correctRow) {
    for (const betKey in detailedBets) {
      const betRows = detailedBets[betKey].rows;
      for (const rowKey in betRows!) {
        const row = betRows![rowKey];
        const correctSigns = calcCorrectSigns(row, correctRow);

        if (correctSigns > maxCorrectSigns) {
          maxCorrectSigns = correctSigns;
        }

        if (!preliminaryOutcome[correctSigns]) {
          preliminaryOutcome[correctSigns] = 1;
        } else {
          preliminaryOutcome[correctSigns] = preliminaryOutcome[correctSigns] + 1;
        }
      }
    }
  }

  let betResult = { amount: 0, currency: round.operatorTurnover.currency };

  if (couponShare!.operatorPreliminaryResult) {
    betResult = couponShare!.operatorPreliminaryResult;
  }

  if (couponShare!.operatorResult) {
    betResult = couponShare!.operatorResult;
  }

  const theme = (window as any).theme;

  let chosenRSystem: System = { type: "MATHEMATICAL" };

  if (couponShare?.coupon?.coupon?.bets) {
    chosenRSystem = couponShare.coupon.coupon.bets[0].system;
  }

  const shareEnabled = IsShareCouponEnabled(theme);

  const cashoutSection = cashout.showCashout && cashout.cashoutValue && (
    <Box sx={{ padding: '12px 16px', display: 'flex', flexDirection: 'column' }}>
      <CashoutButton
        value={cashout.cashoutValue.value}
        disabled={cashout.isCashingOut}
        onClick={cashout.requestCashout}
      />
    </Box>
  );

  const cashoutDialogs = (
    <>
      <CashoutConfirmDialog
        open={cashout.showConfirm}
        value={cashout.cashoutValue?.value}
        onClose={cashout.closeConfirm}
        onConfirm={cashout.confirmCashout}
      />
      <CashoutResultDialog
        result={cashout.result}
        onClose={cashout.closeResult}
      />
    </>
  );

  if (round.name.toLowerCase().indexOf("survivor") !== -1) {
    return (
      <>
        <NavBar whichToUnderline="3" balanceUpdateId={0} />
        <SurvivorCoupon couponShareId={couponShare!.id as any} />
      </>
    );
  }

  const theGameManifest = getTheGameManifest(round);
  if (theGameManifest) {
    return (
      <>
        <NavBar whichToUnderline="3" balanceUpdateId={0} />
        <TheGameLiveCoupon round={round} manifest={theGameManifest} couponShare={couponShare!} />
      </>
    );
  }

  if (isSmallScreen) {
    return (
      <>
        <NavBar whichToUnderline="3" balanceUpdateId={0} />
        <Box sx={wrapperSx}>
          <Box className="left-flex">
            <CouponWinHeader winAmount={betResult} nCorrectRows={maxCorrectSigns} isRightToLeft={isRightToLeft} isHidden={couponShare!.couponHidden} />

            <Box sx={topBorderSx} />

            {/* @ts-ignore - SlimCoupon used in read-only mode with subset of props */}
            <SlimCoupon
              round={round}
              theme={theme}
              coupon={couponShare!.coupon.coupon}
              onChangeFixture={setFixtureValue}
              couponFixtureSummaries={couponShare!.coupon.fixtures}
              chosenRSystem={chosenRSystem}
            />

            {cashoutSection}

            {isAfterDeadline && <WinInfo round={round} couponShare={couponShare!} isRightToLeft={isRightToLeft} />}

            <CouponInfo round={round} couponShare={couponShare!} isRightToLeft={isRightToLeft} shareEnabled={shareEnabled} />

            <BestRows round={round} couponShare={couponShare!} />

            <RowsStatistics round={round} couponShare={couponShare!} />
          </Box>
        </Box>
        {cashoutDialogs}
      </>
    );
  }

  return (
    <>
      <NavBar whichToUnderline="3" balanceUpdateId={0} />
      <Box sx={wrapperSx}>
        <Box className="left-flex">
          <CouponWinHeader winAmount={betResult} nCorrectRows={maxCorrectSigns} isRightToLeft={isRightToLeft} isHidden={couponShare!.couponHidden} />

          <Box sx={topBorderSx} />

          {/* @ts-ignore - SlimCoupon used in read-only mode with subset of props */}
          <SlimCoupon
            round={round}
            theme={theme}
            coupon={couponShare!.coupon.coupon}
            onChangeFixture={setFixtureValue}
            couponFixtureSummaries={couponShare!.coupon.fixtures}
            chosenRSystem={chosenRSystem}
          />

          {cashoutSection}

          {isAfterDeadline && <WinInfo round={round} couponShare={couponShare!} isRightToLeft={isRightToLeft} />}

          <CouponInfo round={round} couponShare={couponShare!} isRightToLeft={isRightToLeft} shareEnabled={shareEnabled} />

          <BestRows round={round} couponShare={couponShare!} />

          <RowsStatistics round={round} couponShare={couponShare!} />
        </Box>
        <Box className="right-flex">
          <ContentArea fixture={fixture} theme={theme} round={round} />
        </Box>
      </Box>
      {cashoutDialogs}
    </>
  );
}
